/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function Hero() {
  const [isConsultOpen, setIsConsultOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsConsultOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <section id="home" className="pt-28 pb-16 bg-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        {/* LEFT CONTENT */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          {/* Item 3: Headline */}
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight tracking-tight">
            Consult a doctor or pharmacist online. Get your medicines delivered.
          </h2>

          {/* Item 4: Subtext */}
          <p className="mt-4 text-gray-600 text-base sm:text-lg leading-relaxed max-w-xl">
            Book a virtual consultation with a licensed doctor or partner pharmacist, get clear advice and a prescription if you need one, then have genuine medicines delivered to your door.
          </p>

          {/* Item 5: Action Buttons */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            {/* Primary Dropdown Button: Consult an Expert */}
            <div className="relative inline-block text-left w-full sm:w-auto" ref={dropdownRef}>
              <button
                onClick={() => setIsConsultOpen(!isConsultOpen)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-primary text-white rounded-xl font-semibold shadow hover:bg-primary/90 transition-all"
              >
                Consult an Expert
                <svg
                  className={`w-4 h-4 transition-transform duration-200 ${
                    isConsultOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <AnimatePresence>
                {isConsultOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="absolute left-0 mt-2 w-full sm:w-72 bg-white border border-gray-100 rounded-2xl shadow-xl z-50 overflow-hidden"
                  >
                    <div className="p-1.5 space-y-1">
                      {/* Doctor WhatsApp Option */}
                      <a
                        href="https://wa.me/2348162738527?text=Hello,%20I%20would%20like%20to%20speak%20with%20a%20doctor."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 hover:bg-slate-50 rounded-xl transition-colors group"
                      >
                        <span className="block font-semibold text-gray-900 group-hover:text-primary transition-colors text-sm">
                          Speak with a Doctor
                        </span>
                        <span className="block text-xs text-gray-500 mt-0.5">
                          Medical advice, diagnosis and prescriptions.
                        </span>
                      </a>

                      {/* Pharmacist WhatsApp Option */}
                      <a
                        href="https://wa.me/2348162738527?text=Hello,%20I%20would%20like%20to%20speak%20with%20a%20pharmacist."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 hover:bg-slate-50 rounded-xl transition-colors group"
                      >
                        <span className="block font-semibold text-gray-900 group-hover:text-primary transition-colors text-sm">
                          Speak with a Pharmacist
                        </span>
                        <span className="block text-xs text-gray-500 mt-0.5">
                          Guidance on your medicines and their use.
                        </span>
                      </a>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Secondary Button: Shop Medicines */}
            <a
              href="#shop"
              className="inline-flex items-center justify-center px-6 py-3.5 border-2 border-primary text-primary rounded-xl font-semibold hover:bg-primary/5 transition-all text-center"
            >
              Shop Medicines
            </a>
          </div>

          {/* Item 6: Trust Badges (4 boxes) */}
          <ul className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-xl">
            {[
              "Licensed doctors",
              "Partner pharmacists",
              "NAFDAC-approved medicines",
              "Private & secure",
            ].map((badgeText, idx) => (
              <motion.li
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white p-3 rounded-xl text-center shadow-sm border border-gray-100 flex items-center justify-center min-h-[54px]"
              >
                <span className="text-xs font-semibold text-gray-800 leading-snug">
                  {badgeText}
                </span>
              </motion.li>
            ))}
          </ul>
        </motion.div>

        {/* RIGHT CONTENT (Kept intact as requested) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="order-last"
        >
          <div className="bg-white rounded-3xl p-5 shadow-soft border border-gray-50">
            <div className="grid grid-cols-2 gap-4">
              {[
                {
                  name: "Paracetamol 500mg",
                  desc: "Pain relief",
                  img: "/parahero.jpg",
                },
                {
                  name: "Amoxicillin 250mg",
                  desc: "Antibiotic",
                  img: "/amoxhero.jpg",
                },
                {
                  name: "Vitamin C",
                  desc: "Supplement",
                  img: "/vitaminc.jpg",
                },
                {
                  name: "Ibuprofen 400mg",
                  desc: "Anti-inflammatory",
                  img: "/ibup.jpg",
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ scale: 1.03 }}
                  className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <img
                    src={item.img}
                    alt={item.name}
                    className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{item.name}</p>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}